import {
  ArrowIcon,
  BreadcrumbHeading,
  IconButton,
  IIconProps,
  SnackbarStatus,
  Spinner,
  Typography,
  useSnackbar,
} from '@weareopenr/component-library';
import { useParams } from 'react-router-dom';
import styles from './MenuPublicationStatus.module.scss';

import { TPublishVenue } from 'src/api/getMenuPublicationStatus/types';
import { ComponentType, useEffect, useState } from 'react';
import { formatDate } from '../../helpers/formatDate/formatDate';
import { formatTime } from '../../helpers/formatTime/formatTime';
import { error as defaultErrorNotification } from '../../mocks/data';

import { useLatestPublicationStatus } from '../../hooks/useLatestPublicationStatus/useLatestPublicationStatus';
import { formatStatus } from '../../helpers/getPublicationStatus/getPublicationStatus';
import { getMenuVenueLogs } from 'src/api/getMenuVenueLogs/getMenuVenueLogs';
import { Routes } from 'src/routes';
import { StatusIcon } from 'src/components/StatusIcon';

const ClosedIcon: ComponentType<IIconProps> = (props) => (
  <ArrowIcon className={styles['closed-icon']} size="small" {...props} />
);

interface VenueRowProps {
  venue: TPublishVenue;
  menuId: string;
}

const VenueRow = ({ venue, menuId }: VenueRowProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [logs, setLogs] = useState<{ id: string; message: string }[]>([]);

  const handleToggleLogs = async () => {
    if (logs.length > 0) {
      setIsVisible((prev) => !prev);
    } else {
      const { messages } = await getMenuVenueLogs(menuId, venue.venue_id, venue.log_id);
      const formattedMessages = messages.map((message) => ({
        id: `${message.entity_id}-${message.timestamp}`,
        message: message.message,
      }));
      setLogs(formattedMessages);
      setIsVisible(true);
    }
  };

  return (
    <div className={styles.venue}>
      <div className={styles['venue-row']}>
        {venue.log_id && (
          <IconButton
            aria-label="View venue logs"
            onClick={handleToggleLogs}
            icon={isVisible ? ArrowIcon : ClosedIcon}
            buttonStyle="ghost"
          />
        )}
        <StatusIcon status={venue.publication_status} animate={true} />
        <div className={styles['venue-info']}>
          <Typography size="small" weight="semi-bold">
            {venue.venue_name}
          </Typography>
          <Typography size="small">{formatStatus(venue.publication_status)}</Typography>
        </div>
      </div>
      {isVisible && (
        <div className={styles['venue-logs']}>
          {logs.map((log) => (
            <Typography key={log.id} size="small">
              {log.message}
            </Typography>
          ))}
        </div>
      )}
    </div>
  );
};

export const MenuPublicationStatus = () => {
  const { menuId } = useParams<{ menuId: string }>();
  const { showSnackbar } = useSnackbar();
  const [hasRefetched, setHasRefetched] = useState<boolean>(false);
  const { data: publicationStatus, error, refetch, isLoading } = useLatestPublicationStatus(menuId);

  useEffect(() => {
    if (error) {
      showSnackbar({ status: SnackbarStatus.ERROR, message: defaultErrorNotification });
    }
  }, [error]);

  useEffect(() => {
    // This forces a second call if the menu is unpublished.
    // The publication flow is async and can return incorrect data when changing
    // quickly between the publication flow and status page
    if (publicationStatus?.publication_status === 'UNPUBLISHED' && !hasRefetched) {
      setHasRefetched(true);
      setTimeout(() => {
        refetch();
      }, 1000);
    }
  }, [publicationStatus]);

  return (
    <>
      <BreadcrumbHeading
        breadcrumbs={[
          { label: 'Menus', path: Routes.MENUS },
          { label: publicationStatus?.menu_name, path: `${Routes.MENUS}/${publicationStatus?.menu_id}` },
          { label: 'Publication Status', path: location.pathname },
        ]}
        subtitle={
          publicationStatus?.publication_date
            ? `Published: ${formatDate(new Date(publicationStatus.publication_date))}, ${formatTime(
                new Date(publicationStatus.publication_date),
              )}`
            : 'This menu has not been published'
        }
      />
      {isLoading ? (
        <div className={styles.spinner}>
          <Spinner size="xlarge" colour="rainbow" />
        </div>
      ) : (
        <div className={styles['status-container']}>
          {publicationStatus?.external_systems
            .filter(
              (system, index, self) =>
                index === self.findIndex((s) => s.external_system_id === system.external_system_id),
            )
            .map((externalSystem, index) => {
              return (
                <div key={`${externalSystem.external_system_id}-${index}`}>
                  <div className={styles.channel}>
                    <StatusIcon status={externalSystem.publication_status} animate />
                    <div>
                      <Typography size="small" weight="semi-bold">
                        {externalSystem.external_system_name}
                      </Typography>
                      <Typography size="small">{formatStatus(externalSystem.external_system_name)}</Typography>
                      {externalSystem.messages?.map((message) => (
                        <Typography key={`${message.entity_id}-${message.log_timestamp}`} size="small">
                          {message.message}
                        </Typography>
                      ))}
                    </div>
                  </div>
                  <div className={styles.venues}>
                    {externalSystem.venues.map((venue) => (
                      <VenueRow
                        key={`${externalSystem.external_system_id}-${venue.venue_id}`}
                        venue={venue}
                        menuId={menuId}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </>
  );
};
